import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarCheck2, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/sales`;

const SPORTS = ['Basketball', 'Football', 'Soccer', 'Baseball', 'Softball', 'Volleyball', 'Other'];
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN',
  'TX','UT','VT','VA','WA','WV','WI','WY'];

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '', state: '', school: '', sport: '',
  role: '', preferred_contact_method: '', preferred_call_time: '', message: '', consent: false,
  sms_opt_in: false, website: '',
};

export default function RequestDemoPage() {
  const { leadId: routeLeadId } = useParams();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { lead_id, existing_customer, resubmitted }
  const [slots, setSlots] = useState(null); // null = not loaded, [] or array, or 'manual'
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);
  const [leadId, setLeadId] = useState(routeLeadId || null);

  const set = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  useEffect(() => {
    if (routeLeadId) {
      setResult({ lead_id: routeLeadId });
      loadSlots(routeLeadId);
    }
  }, [routeLeadId]);

  const loadSlots = async (id) => {
    try {
      const res = await axios.get(`${API}/leads/${id}/slots`);
      setSlots(res.data.manual ? 'manual' : res.data.slots);
    } catch {
      setSlots('manual');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent) {
      setError('Please agree to be contacted before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/request`, form);
      setResult(res.data);
      if (res.data.lead_id && !res.data.existing_customer) {
        setLeadId(res.data.lead_id);
        loadSlots(res.data.lead_id);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const bookSlot = async (startsAt) => {
    const id = leadId || routeLeadId;
    setBooking(true);
    try {
      await axios.post(`${API}/leads/${id}/book`, { starts_at: startsAt });
      setBooked(startsAt);
    } catch {
      setError('That slot may already be taken — please pick another.');
      loadSlots(id);
    } finally {
      setBooking(false);
    }
  };

  const fmtSlot = (iso) => new Date(iso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  if (result) {
    return (
      <div className="min-h-screen bg-[#0b1120] text-white flex items-center justify-center p-6" data-testid="request-demo-confirmation">
        <Card className="max-w-lg w-full bg-[#111827] border-white/10 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" /> Thanks{form.first_name ? `, ${form.first_name}` : ''} — your request is in
            </CardTitle>
            <CardDescription className="text-white/60">
              {result.existing_customer
                ? "We see you're already a PAC customer — your account team has been notified."
                : result.resubmitted
                  ? 'Your existing request has been refreshed. We\'ll be in touch shortly.'
                  : 'Pick a time that works for you, or we\'ll reach out within one business day.'}
            </CardDescription>
          </CardHeader>
          {!result.existing_customer && !result.resubmitted && (
            <CardContent className="space-y-4">
              {booked ? (
                <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-4 text-emerald-300" data-testid="request-demo-booked-message">
                  <p className="font-medium flex items-center gap-2"><CalendarCheck2 className="h-4 w-4" /> You're set: {fmtSlot(booked)}.</p>
                  <p className="text-sm mt-1 text-emerald-300/80">A calendar invite with a video link is on its way to {form.email || 'your email'}.</p>
                </div>
              ) : slots === null ? (
                <p className="text-white/50 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading available times…</p>
              ) : slots === 'manual' || !slots?.length ? (
                <div className="rounded-md bg-white/5 border border-white/10 p-4 text-white/70 text-sm" data-testid="request-demo-manual-message">
                  <Clock className="h-4 w-4 inline mr-1.5 -mt-0.5" /> A PAC team member will reach out within one business day to set up a time.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="request-demo-slots-list">
                  {slots.map((s) => (
                    <Button key={s.starts_at} variant="outline" disabled={booking}
                            data-testid={`request-demo-slot-${s.starts_at}`}
                            className="justify-start border-white/15 text-white hover:bg-white/10"
                            onClick={() => bookSlot(s.starts_at)}>
                      {fmtSlot(s.starts_at)}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1120] text-white py-10 px-4" data-testid="request-demo-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <p className="text-xs tracking-widest text-amber-400 font-mono">PERSONAL AI COACH</p>
          <h1 className="text-3xl font-bold mt-1">Talk to PAC Sales</h1>
          <p className="text-white/60 mt-2">Tell us about your program — takes under a minute.</p>
        </div>
        <Card className="bg-[#111827] border-white/10">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-5" data-testid="request-demo-form">
              {/* Honeypot — hidden from real users, bots often fill every field */}
              <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                <Label htmlFor="website">Website</Label>
                <Input id="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website')(e.target.value)} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">First name*</Label>
                  <Input required data-testid="request-demo-first-name" value={form.first_name} onChange={(e) => set('first_name')(e.target.value)}
                         className="bg-white/5 border-white/15 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-white/80">Last name*</Label>
                  <Input required data-testid="request-demo-last-name" value={form.last_name} onChange={(e) => set('last_name')(e.target.value)}
                         className="bg-white/5 border-white/15 text-white mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Email*</Label>
                  <Input required type="email" data-testid="request-demo-email" value={form.email} onChange={(e) => set('email')(e.target.value)}
                         className="bg-white/5 border-white/15 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-white/80">Phone*</Label>
                  <Input required type="tel" placeholder="+1 214 555 1234" data-testid="request-demo-phone" value={form.phone}
                         onChange={(e) => set('phone')(e.target.value)} className="bg-white/5 border-white/15 text-white mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">State*</Label>
                  <Select value={form.state} onValueChange={set('state')}>
                    <SelectTrigger data-testid="request-demo-state" className="bg-white/5 border-white/15 text-white mt-1"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="max-h-64">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/80">School/Organization*</Label>
                  <Input required data-testid="request-demo-school" value={form.school} onChange={(e) => set('school')(e.target.value)}
                         className="bg-white/5 border-white/15 text-white mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Sport*</Label>
                  <Select value={form.sport} onValueChange={set('sport')}>
                    <SelectTrigger data-testid="request-demo-sport" className="bg-white/5 border-white/15 text-white mt-1"><SelectValue placeholder="Select sport" /></SelectTrigger>
                    <SelectContent>{SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/80">Your role</Label>
                  <Input data-testid="request-demo-role" value={form.role} onChange={(e) => set('role')(e.target.value)}
                         className="bg-white/5 border-white/15 text-white mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Preferred contact method</Label>
                  <Select value={form.preferred_contact_method} onValueChange={set('preferred_contact_method')}>
                    <SelectTrigger data-testid="request-demo-contact-method" className="bg-white/5 border-white/15 text-white mt-1"><SelectValue placeholder="No preference" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="Phone">Phone</SelectItem>
                      <SelectItem value="Text">Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/80">Preferred call time (Central)</Label>
                  <Input data-testid="request-demo-call-time" placeholder="e.g. Weekday afternoons" value={form.preferred_call_time}
                         onChange={(e) => set('preferred_call_time')(e.target.value)} className="bg-white/5 border-white/15 text-white mt-1" />
                </div>
              </div>

              <div>
                <Label className="text-white/80">Message</Label>
                <Textarea maxLength={500} rows={3} data-testid="request-demo-message" value={form.message}
                          onChange={(e) => set('message')(e.target.value)} className="bg-white/5 border-white/15 text-white mt-1" />
                <p className="text-xs text-white/40 mt-1">{form.message.length}/500</p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-2">
                  <Checkbox id="consent" data-testid="request-demo-consent" checked={form.consent}
                            onCheckedChange={(v) => set('consent')(!!v)} className="mt-0.5" />
                  <Label htmlFor="consent" className="text-sm text-white/70 font-normal">
                    I agree to be contacted by PAC about my request. See our Terms and Privacy Policy.*
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox id="sms_opt_in" data-testid="request-demo-sms-opt-in" checked={form.sms_opt_in}
                            onCheckedChange={(v) => set('sms_opt_in')(!!v)} className="mt-0.5" />
                  <Label htmlFor="sms_opt_in" className="text-sm text-white/70 font-normal">
                    It's okay to text me about my request.
                  </Label>
                </div>
              </div>

              {error && <p className="text-sm text-red-400" data-testid="request-demo-error">{error}</p>}

              <Button type="submit" disabled={submitting} data-testid="request-demo-submit-btn"
                      className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : 'Talk to PAC Sales'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
